import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox'
import { NON_EU_COUNTRIES } from '@/shared/config/countries'
import { cn } from '@/shared/lib/utils'

/** Lowercase ISO codes from {@link NON_EU_COUNTRIES} only; stable (labels from i18n). */
const COUNTRY_CODES_SORTED = (() => {
  const seen = new Set<string>()
  const codes: string[] = []
  for (const { value: code } of NON_EU_COUNTRIES) {
    const lower = code.toLowerCase()
    if (!seen.has(lower)) {
      seen.add(lower)
      codes.push(lower)
    }
  }
  return codes
})()

type CountryOption = {
  value: string
  label: string
}

type InstallationCountrySelectProps = {
  value: string
  onValueChange: (isoAlpha2UpperCase: string) => void
  placeholder: string
  disabled?: boolean
  id?: string
  triggerClassName?: string
  'aria-invalid'?: boolean
}

function matchesCountryQuery(option: CountryOption, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) {
    return true
  }
  return (
    option.label.toLowerCase().includes(trimmed) ||
    option.value.toLowerCase().includes(trimmed)
  )
}

/**
 * Searchable non-EU country list from `countries.ts` with localized names
 * (`countriesByIsoAlpha2`).
 */
export function InstallationCountrySelect({
  value,
  onValueChange,
  placeholder,
  disabled,
  id,
  triggerClassName,
  'aria-invalid': ariaInvalid,
}: InstallationCountrySelectProps) {
  const { i18n, t } = useTranslation('countriesByIsoAlpha2')

  const options = useMemo(() => {
    const rows = COUNTRY_CODES_SORTED.map((code) => ({
      value: code,
      label: t(code),
    }))

    rows.sort((optionA, optionB) =>
      optionA.label.localeCompare(optionB.label, undefined, {
        sensitivity: 'base',
      })
    )

    return rows
  }, [t])

  const selectValue = value.trim().length === 2 ? value.trim().toLowerCase() : undefined

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectValue) ?? null,
    [options, selectValue]
  )

  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    if (selectedOption) {
      setInputValue(selectedOption.label)
    } else {
      setInputValue('')
    }
  }, [selectedOption])

  const handleOpenChangeComplete = useCallback(
    (open: boolean) => {
      if (!open && selectedOption) {
        setInputValue(selectedOption.label)
      }
    },
    [selectedOption]
  )

  return (
    <Combobox
      items={options}
      value={selectedOption}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      onOpenChangeComplete={handleOpenChangeComplete}
      disabled={disabled}
      locale={i18n.language}
      autoHighlight
      itemToStringLabel={(option) => option.label}
      isItemEqualToValue={(optionA, optionB) => optionA.value === optionB.value}
      filter={(item, query) => matchesCountryQuery(item as CountryOption, query)}
      onValueChange={(option) => {
        if (option) {
          onValueChange(option.value.toUpperCase())
        }
      }}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        className={cn('h-9 w-full min-w-0', triggerClassName)}
      />
      <ComboboxContent className="max-h-[min(24rem,var(--available-height))]">
        <ComboboxEmpty>{t('noSearchResults')}</ComboboxEmpty>
        <ComboboxList>
          {(option: CountryOption) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
